import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { Box, IconButton, Paper, Typography, useTheme } from '@mui/material';
import { Dispatch, PropsWithChildren, SetStateAction, useEffect, useState } from 'react';

const PANEL_WIDTH = '406px';
const PANEL_PADDING_RIGHT = '6px';

type SidebarProps = PropsWithChildren<{
  isOpen: boolean;
  setIsOpen?: Dispatch<SetStateAction<boolean>>;
  position?: 'left' | 'right';
  floating?: boolean;
  title: string | React.JSX.Element;
  testId?: string;
  openPanelVisible?: boolean;
  noTopBorder?: boolean;
  noDropShadow?: boolean;
}>;

export default function Sidebar({
  isOpen,
  setIsOpen,
  children,
  floating,
  title,
  testId,
  openPanelVisible,
  noTopBorder,
  position,
  noDropShadow,
}: SidebarProps) {
  const theme = useTheme();

  const [isContentMounted, setIsContentMounted] = useState(isOpen);

  // While we use this sidebar component as the sidebar
  // this allows us to unmount the content after the sidebar has closed (width transition 0.5s).
  /* Deferring unmount until the close transition finishes is intentional. */
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    if (isOpen === true) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- mount content when the sidebar opens
      setIsContentMounted(true);
    }

    if (isOpen === false) {
      timeout = setTimeout(() => {
        setIsContentMounted(false);
      }, 500);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isOpen]);

  return (
    <Paper
      tabIndex={isOpen ? 0 : undefined}
      style={{
        transition: 'width 0.5s',
        width: isOpen ? PANEL_WIDTH : openPanelVisible ? '40px' : '0px',
        height: 'inherit',
        overflowX: 'hidden',
        overflowY: isOpen ? 'auto' : 'hidden',
        position: floating ? 'absolute' : 'unset',
        left: position === 'left' ? '0' : undefined,
        right: position === 'right' ? '0' : undefined,
        zIndex: 500,
        borderRadius: 0,
        flexShrink: 0,
        borderLeft: position === 'right' ? '1px solid #0000001f' : '',
        borderRight: position === 'left' ? '1px solid #0000001f' : '',
        boxShadow: noDropShadow ? '' : '0px 6px 4px #00000040',
      }}
      data-testid={testId}
      elevation={floating ? 2 : 0}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: 'center',
          paddingY: '1rem',
          paddingX: !isOpen && openPanelVisible ? '0' : '1rem',
          height: '4rem',
          width: isOpen ? PANEL_WIDTH : '40px',
          color: theme.palette.primary.dark,
          transition: 'padding 0.5s, width 0.5s'
        }}>
        {title && typeof title === 'string' ? (
          <Typography
            component="h2"
            variant="h5"
            sx={[
              {
                flexShrink: 1,
                maxHeight: '2rem',
                transition: 'opacity 0.3s',
                overflow: 'hidden',
              },
              isOpen
                ? {
                    opacity: '1',
                  }
                : {
                    opacity: '0',
                  },
            ]}
          >
            {title}
          </Typography>
        ) : (
          title
        )}
        {setIsOpen && (
          <IconButton
            sx={{ backgroundColor: 'transparent', flexShrink: 0 }}
            color="inherit"
            onClick={() => setIsOpen((prev) => !prev)}
            aria-label={`Toggle ${title && typeof title === 'string' ? title : ''} panel`}
          >
            {isOpen ? <ArrowBack /> : <ArrowForward />}
          </IconButton>
        )}
      </Box>
      <Box
        sx={[
          {
            transition: 'opacity 0.3s',
            width: PANEL_WIDTH,
            paddingRight: PANEL_PADDING_RIGHT,
          },
          isOpen
            ? {
                opacity: '1',
              }
            : {
                opacity: '0',
              },
          noTopBorder
            ? {
                borderTop: '',
              }
            : {
                borderTop: '1px solid #0000001f',
              },
        ]}
      >
        {(isOpen || isContentMounted) && children}
      </Box>
    </Paper>
  );
}
